import React from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from 'components';
import { Button } from 'design-system-web';
import { HookFormInput, HookFormTextarea } from 'components/reactHookForm';
import { useTranslation } from 'react-i18next';
import type { CostOffsetCode } from 'domains-billing-types';
import styles from '../styles.scss';

const DESCRIPTION_MAX_LENGTH = 200;
const DESCRIPTION_MIN_ROWS = 4;

export type CostOffsetCodeFormValues = Pick<
  CostOffsetCode,
  'code' | 'description'
>;

type CostOffsetCodeFormProps = {
  costOffsetCodeData?: CostOffsetCodeFormValues;
  error?: {
    severity: 'warning' | 'error';
    error: Error;
  };
  disabled?: boolean;
  onSubmit: (data: CostOffsetCodeFormValues) => Promise<void>;
  onCancel: () => void;
};

export function CostOffsetCodeForm({
  costOffsetCodeData,
  error,
  disabled,
  onSubmit,
  onCancel,
}: CostOffsetCodeFormProps) {
  const { t } = useTranslation();

  const { control, handleSubmit, watch } = useForm<CostOffsetCodeFormValues>({
    mode: 'onChange',
    values: {
      code: costOffsetCodeData?.code || '',
      description: costOffsetCodeData?.description || '',
    },
  });

  const watchedCode = watch('code');

  const isSaveDisabled = !watchedCode?.trim() || disabled || error?.severity === 'error';

  return (
    <>
      <div className={styles.editChargeCodeForm}>
        <fieldset className={styles.fieldset}>
          <label className={styles.label}>
            <span className={styles.labelText}>
              {t('abp.productsServices.createCostOffsetCode.code')}
              <span className="ml-0.5 text-[#DA5A4E]">*</span>
            </span>
          </label>
          <HookFormInput
            control={control}
            name="code"
            type="text"
            isRequired
            testId="code"
          />
        </fieldset>

        <fieldset className={styles.fieldset}>
          <label className={styles.label}>
            {t('abp.productsServices.createCostOffsetCode.description')}
          </label>
          <HookFormTextarea
            control={control}
            name="description"
            maxLength={DESCRIPTION_MAX_LENGTH}
            showLengthIndicator
            minRows={DESCRIPTION_MIN_ROWS}
            testId="description"
          />
        </fieldset>
      </div>

      {error && <ErrorMessage error={error.error.message} />}

      <div className={styles.formButtons}>
        <Button
          className={styles.button}
          variant="primary"
          size="large"
          disabled={isSaveDisabled}
          onClick={handleSubmit(onSubmit)}
        >
          {t('abp.productsServices.createCostOffsetCode.save')}
        </Button>
        <Button
          className={styles.button}
          variant="secondary"
          size="large"
          onClick={onCancel}
          disabled={disabled}
        >
          {t('abp.productsServices.createCostOffsetCode.cancel')}
        </Button>
      </div>
    </>
  );
}
